import { ThemeOptions } from '@mui/material/styles'
import { space } from '../utils/spacing'
import { colors, general } from './config'
import { baseColors } from '../base/config'
import shadows from '@mui/material/styles/shadows'

/**
 * E3DC theme options.
 */
export const e3dcThemeOptions: ThemeOptions = {
  space: (spacing: number) => space(spacing, general.spacingBase),
  effectStyles: {
    shadowMain: '0px 4px 12px 0px rgba(0, 0, 0, 0.1)',
    shadowStrong: '0px 4px 5px 0px rgba(0, 0, 0, 0.2)',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1100,
      lg: 1400,
      xl: 1920,
    },
  },
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1000,
    bottomBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
  components: {
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true,
      },
    },
    MuiSwitch: {
      defaultProps: {
        color: 'primary',
      },
    },
    MuiRadio: {
      defaultProps: {
        color: 'primary',
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        variant: 'contained',
        color: 'secondary',
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiFormControl: {
      defaultProps: {
        fullWidth: true,
        // @ts-expect-error align does not exist
        align: 'left', // theres a ...  maybe bug...  where <grid container align="center"> with textfields that are outlined need textAlign set to left! otherwise the hole in the legend is centered ...
      },
    },
    MuiPaper: {
      defaultProps: {
        square: true,
      },
    },
    MuiGrid: {
      defaultProps: {
        spacing: 2,
      },
    },
  },
  typography: {
    fontFamily: 'Roboto, HagerScale, Helvetica, Arial, sans-serif',
    h1: {
      fontSize: 28,
      lineHeight: 1.285,
      fontWeight: 700,
      letterSpacing: 0,
    },
    h2: {
      fontSize: 22,
      lineHeight: 1.27,
      letterSpacing: 0,
      fontWeight: 400,
    },
    h3: {
      fontSize: 16,
      lineHeight: 1.25,
      letterSpacing: '0.1px',
      fontWeight: 500,
    },
    h4: {
      fontSize: 16,
      lineHeight: 1.5,
      letterSpacing: '0.5px',
      fontWeight: 400,
    },
    h5: {
      fontSize: 14,
      lineHeight: 1.43,
      letterSpacing: '0.1px',
      fontWeight: 700,
    },
    h6: {
      fontWeight: 500,
    },
    subtitle1: {
      fontSize: 14,
      lineHeight: 1.43,
      letterSpacing: '0.1px',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: 12,
      lineHeight: 1.34,
      letterSpacing: '0.5px',
      fontWeight: 400,
    },
    overline: {
      fontSize: 11,
      lineHeight: 1.45,
      textTransform: 'uppercase',
      fontWeight: 500,
      letterSpacing: '0.5px',
    },
    body1: {
      fontSize: 16,
      lineHeight: 1.5,
      letterSpacing: '0.5px',
      fontWeight: 400,
    },
    body2: {
      fontSize: 14,
      lineHeight: 1.29,
      letterSpacing: '0.5px',
      fontWeight: 400,
    },
    button: {
      fontSize: 14,
      lineHeight: 1,
      letterSpacing: '0.25px',
      fontWeight: 400,
      textTransform: 'inherit',
    },
    caption: {
      fontSize: 11,
      lineHeight: 1.45,
      letterSpacing: '0.4px',
      fontWeight: 400,
    },
    headerCategoryTitle: {
      fontSize: 11,
      lineHeight: 1.45,
      letterSpacing: 0.5,
      fontWeight: 400,
    },
    accordionTitle: {
      variant: 'h3',
    },
    paper: 'h3',
    sidebar: 'body2',
    dialog: 'h3',
  },
  shape: {
    dialog: {
      title: {
        minHeight: 42,
      },
    },
    borderRadius: general.defaultBorderRadius,
    customBorderRadius: {
      tabButton: 6,
      tabButtonGroup: 12,
      progressBar: 6,
    },
    dialogDivider: 0,
    accordion: {
      arrowSize: 24,
    },
    secondaryAction: {
      color: baseColors.grey.grey04,
      borderColor: baseColors.grey.grey04,
    },
    inputField: {
      height: general.spacingBase * 6,
      labelFontSize: 16,
      labelFontSizeShrinked: 14,
      labelColor: '#49454F',
      labelColorActive: colors.primary.dark,
      labelFontWeightShrinked: 'normal',
      labelActiveTransform: 'translate(14px, -5px) scale(0.87)',
      labelActiveLeft: -9,
      labelFloatingActive: `translate(calc(14px - (${general.spacingBase}px / 2)), -6px) scale(0.78)`,
      labelFloatingInactive: `translate(calc(14px - (${general.spacingBase}px / 2)), -50%) scale(1)`,
      disabledBackgroundColor: baseColors.grey.grey06,
      fontSize: 14,
    },
    markdownEditor: {
      shadowPopover: '0px 5px 10px rgba(0, 0, 0, 0.3)',
      background: colors.white,
      editorShellColor: '#000',
      draggableButtonBorderRadius: '4px',
      draggableButtonBackground: baseColors.grey.grey11,
      defaultFontWeight: 400,
      defaultFontSize: 14,
      tableHeaderBackground: '#dfdfdf',
    },
    select: {
      dropdownIndicatorColor: colors.primary.dark,
      controlIsFocusedColor: colors.primary.dark,
      paddingLeft: 12,
      labelFontSize: 14,
      labelFontSizeDisabled: 14,
      labelBackground: colors.white,
      textColor: baseColors.grey.grey00,
      fontSize: 14,
    },
    dayButton: {
      size: 36,
      sizeSm: 36,
    },
    logoutButton: {
      color: 'inherit',
    },
    sidebar: {
      borderRadius: 0,
      height: '100%',
      top: 0,
      bottom: 0,
      left: 0,
      drawerWidth: 264,
      drawerCollapsedWidth: 72,
    },
    languageSelector: {
      backgroundColor: baseColors.grey.grey01,
    },
    privateInstallationCard: {
      boxShadow: shadows[2],
    },
    header: {
      left: 256 + 24, //drawerWidth + spacing
      leftCollapsed: 72 + 24, //drawerCollapsedWidth + spacing
      right: 12,
      top: 12,
      borderRadius: 12,
      /** height = header component height + top position */
      height: 66,
      mobileHeight: 59,
      padding: 12,
      marginBottom: 6,
    },
    tabs: {
      topMargin: 0,
      height: 27,
    },
    paper: {
      icon: {
        width: 12,
      },
      headline: {
        marginLeft: general.spacingBase * 2,
      },
    },
    filter: {
      height: 36,
      menuItemHeight: 36,
      searchFieldHeight: 36,
    },
    bottomLabel: {
      disabledColor: baseColors.grey.grey02,
      nonSelectedColor: baseColors.grey.grey05,
    },
    searchField: {
      hoverBorderColor: colors.primary.main,
      maxWidth: '300px',
    },
    snackbar: {
      borderRadius: 12,
      color: {
        warningDark: colors.warning,
        errorDark: colors.error,
        successDark: colors.primary.dark,
        informationDark: '#000000',
      },
      backgroundColor: {
        warningLight: baseColors.warning.light,
        errorLight: baseColors.error.light,
        successLight: colors.primary.light,
        informationLight: colors.white,
      },
    },
    stepIconComponent: {
      color: {
        errorLight: baseColors.error.light,
        errorDark: colors.error,
        warningLight: baseColors.warning.light,
      },
    },
    dateRange: {
      activeTextFontWeight: 'bold',
    },
    errorBanner: {
      background: baseColors.error.light,
    },
    recomission: {
      endDateColor: '#BF360C',
    },
    liveViewIconContainer: {
      borderWidth: 4,
      borderStyle: 'solid',
      borderColor: colors.primary.main,
      borderRadius: '10px',
    },
    liveViewEnergyFlowIndictor: {
      borderRadius: 12,
    },
    mobileDrawer: {
      borderRadius: '12px 12px 0 0',
    },
  },
  mixins: {
    toolbar: {
      minHeight: 48,
    },
  },
  spacing: general.spacingBase,
}
